import { authFetch, BASE_BACKEND_URL } from '../../auth'
import React, { useEffect, useMemo, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useLocation, useNavigate } from 'react-router-dom'

import ImageModal from '../../components/image_modal/ImageModal'
import ConfirmDialog from '../../components/dialogs/ConfirmDialog'
import { layoutMobileMediaQuery } from '../../layout/layoutConstants'
import { canManagePosTasks } from '../../global_context/capabilities/posCapabilities'
import { UI_COLOR_DANGER_ACTION } from '../../constants/uiColors'
import PosDetailsSurvey from './PosDetailsSurvey'
import PosDetailsDesktop from './desktop/PosDetailsDesktop'
import PosDetailsMobile from './mobile/PosDetailsMobile'
import {
  buildPosTaskAssignmentFields,
  buildPosTaskExtraFields,
  buildPosTaskHeaderData,
  buildPosTaskScopeFields,
  buildPosTaskSummaryFields,
  getPosTaskExtraTitle,
  getPosTaskStatusUI,
  normalizeCompetitionProducts,
  renderPosTaskAssignmentEmptyState
} from './shared/posDetailsHelpers'

const PosDetailsTab = ({ activity: initialActivity, transactionUuid, onActivityChange }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)
  const [activity, setActivity] = useState({
    ...(initialActivity || {}),
    competition_products: normalizeCompetitionProducts(initialActivity?.competition_products)
  })
  const [suspending, setSuspending] = useState(false)
  const [showImage, setShowImage] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState(null)

  useEffect(() => {
    setActivity({
      ...(initialActivity || {}),
      competition_products: normalizeCompetitionProducts(initialActivity?.competition_products)
    })
  }, [initialActivity])

  const headerData = useMemo(() => buildPosTaskHeaderData(activity), [activity])
  const summaryFields = useMemo(() => buildPosTaskSummaryFields(activity), [activity])
  const scopeFields = useMemo(() => buildPosTaskScopeFields(activity), [activity])
  const assignmentFields = useMemo(() => buildPosTaskAssignmentFields(activity), [activity])
  const extraTitle = useMemo(() => getPosTaskExtraTitle(activity), [activity])
  const extraFields = useMemo(
    () =>
      buildPosTaskExtraFields(activity, {
        onShowImage: setShowImage
      }),
    [activity]
  )
  const extraContent =
    activity.task_type === 'survey' ? <PosDetailsSurvey data={activity.surveys} /> : null
  const statusUI = useMemo(() => getPosTaskStatusUI(activity), [activity])
  const returnToListPath = location.state?.from || '/ejecucion_punto_venta'
  const showActions = Boolean(transactionUuid) && canManagePosTasks()

  const closeConfirmDialog = () => {
    if (!suspending) setConfirmDialog(null)
  }

  const confirmDialogAction = () => {
    const onConfirm = confirmDialog?.onConfirm
    setConfirmDialog(null)
    onConfirm?.()
  }

  const confirmSuspendTask = () => {
    setSuspending(true)
    authFetch(`${BASE_BACKEND_URL}/pos_tasks/${activity.uuid}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        suspended: true,
        uuid: activity.uuid
      })
    })
      .then((response) => {
        if (!response) return null
        return response.json()
      })
      .then((data) => {
        if (data?.suspended) {
          const nextActivity = {
            ...data,
            competition_products: normalizeCompetitionProducts(data.competition_products)
          }
          setActivity(nextActivity)
          onActivityChange?.(nextActivity)
          if (location.search.includes('success=true')) {
            navigate(location.pathname, { replace: true, state: location.state })
          }
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSuspending(false))
  }

  const suspendTask = () => {
    setConfirmDialog({
      title: '¿Suspender tarea?',
      message: '¿Está seguro de que desea continuar con la suspensión de esta tarea?',
      confirmLabel: 'Suspender',
      confirmColor: UI_COLOR_DANGER_ACTION,
      onConfirm: confirmSuspendTask
    })
  }

  const cloneTask = () => {
    setConfirmDialog({
      title: '¿Crear copia de tarea?',
      message: 'Se creará una nueva tarea a partir de esta tarea.',
      confirmLabel: 'Crear copia',
      onConfirm: () => {
        navigate(`/crear_tarea_punto_venta?clone=${activity.uuid}`, {
          state: { from: returnToListPath }
        })
      }
    })
  }

  const layoutProps = {
    headerData,
    summaryFields,
    scopeFields,
    assignmentFields,
    assignmentEmptyContent: renderPosTaskAssignmentEmptyState(),
    extraTitle,
    extraFields,
    extraContent,
    statusUI,
    activity,
    showActions,
    suspending,
    onSuspend: suspendTask,
    onClone: cloneTask
  }

  return (
    <>
      {isMobileLayout ? (
        <PosDetailsMobile {...layoutProps} />
      ) : (
        <PosDetailsDesktop {...layoutProps} />
      )}

      {activity.task_type === 'planimetry' && activity.image_url && (
        <ImageModal imgSrc={activity.image_url} showImage={showImage} setShowImage={setShowImage} />
      )}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Aceptar'}
        confirmColor={confirmDialog?.confirmColor}
        disableClose={suspending}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialogAction}
      />
    </>
  )
}

export default PosDetailsTab
